import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Form, Button, Row, Col, Card, ProgressBar } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getAssessmentCriteria, createAssessment, getAssessmentScore } from '../../services/assessmentService';

const AssessmentForm = ({ housingDataId, onSuccess, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [criteria, setCriteria] = useState([]);
  const [score, setScore] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm();

  const watchedValues = watch();

  useEffect(() => {
    const loadCriteria = async () => {
      try {
        const criteriaData = await getAssessmentCriteria();
        setCriteria(criteriaData);
      } catch (error) {
        toast.error('Gagal memuat kriteria penilaian');
      }
    };

    const loadExistingAssessment = async () => {
      try {
        const assessment = await getAssessmentScore(housingDataId);
        if (assessment) {
          // Pre-fill form with existing data
          Object.keys(assessment).forEach(key => {
            if (assessment[key] !== null && assessment[key] !== undefined) {
              setValue(key, assessment[key]);
            }
          });
        }
      } catch (error) {
        // Assessment doesn't exist yet, that's okay
      }
    };

    loadCriteria();
    loadExistingAssessment();
  }, [housingDataId, setValue]);

  const calculateScore = useCallback((values) => {
    let totalScore = 0;
    let totalWeight = 0;

    criteria.forEach(criterion => {
      const value = values[criterion.field_name];
      if (value && criterion.weight) {
        totalScore += (parseFloat(value) * criterion.weight);
        totalWeight += criterion.weight;
      }
    });

    return totalWeight > 0 ? (totalScore / totalWeight) : 0;
  }, [criteria]);

  useEffect(() => {
    const currentScore = calculateScore(watchedValues);
    setScore(currentScore);
  }, [watchedValues, criteria, calculateScore]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await createAssessment(housingDataId, data);
      toast.success('Penilaian berhasil disimpan');
      onSuccess();
    } catch (error) {
      toast.error('Gagal menyimpan penilaian');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'danger';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Layak Huni';
    if (score >= 60) return 'Kurang Layak Huni';
    return 'Tidak Layak Huni';
  };

  if (criteria.length === 0) {
    return <div className="loading-spinner">Loading kriteria penilaian...</div>;
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Penilaian Kelayakan Huni</h2>
        {score !== null && (
          <div className="text-center">
            <h4>Skor Kelayakan</h4>
            <ProgressBar 
              now={score} 
              variant={getScoreColor(score)}
              className="mb-2"
              style={{ height: '30px' }}
            />
            <h3 className={`text-${getScoreColor(score)}`}>
              {score.toFixed(1)} - {getScoreLabel(score)}
            </h3>
          </div>
        )}
      </div>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          {criteria.map((criterion, index) => (
            <Col md={6} key={criterion.id}>
              <Card className="mb-3">
                <Card.Header>
                  <h6>{criterion.name}</h6>
                  <small className="text-muted">Bobot: {criterion.weight}</small>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>{criterion.description}</Form.Label>
                    <Form.Select
                      {...register(criterion.field_name, { 
                        required: `${criterion.name} wajib dipilih` 
                      })}
                      isInvalid={!!errors[criterion.field_name]}
                    >
                      <option value="">Pilih {criterion.name}</option>
                      {criterion.options?.map((option, optIndex) => (
                        <option key={optIndex} value={option.value}>
                          {option.label} ({option.value})
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors[criterion.field_name]?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row>
          <Col md={12}>
            <Card className="mb-3">
              <Card.Header>
                <h5>Keterangan Tambahan</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Catatan Penilaian</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    {...register('catatan_penilaian')}
                    placeholder="Tambahkan catatan atau keterangan tambahan tentang penilaian..."
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Rekomendasi</Form.Label>
                  <Form.Select {...register('rekomendasi')}>
                    <option value="">Pilih Rekomendasi</option>
                    <option value="Perbaikan Ringan">Perbaikan Ringan</option>
                    <option value="Perbaikan Sedang">Perbaikan Sedang</option>
                    <option value="Perbaikan Berat">Perbaikan Berat</option>
                    <option value="Pembangunan Ulang">Pembangunan Ulang</option>
                    <option value="Tidak Perlu Perbaikan">Tidak Perlu Perbaikan</option>
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="d-flex justify-content-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Batal
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Menyimpan...' : 'Simpan Penilaian'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AssessmentForm; 